import { request } from './client.js'

/** Urutan level SKDI dari terendah ke tertinggi; sama dengan sisi server. */
export const SKDI_LEVELS: readonly string[] = ['1', '2', '3A', '3B', '4A', '4B']

export interface Requirement {
  readonly id?: string
  readonly category: string
  readonly minStations: number
  readonly minSkdiLevel: string | null
}

export interface Blueprint {
  readonly id: string
  readonly name: string
  readonly rotations: number
  readonly notes: string | null
  readonly createdBy: string
  readonly createdAt: string
  readonly requirements: readonly Requirement[]
}

export interface Category {
  readonly category: string
  readonly stations: number
  readonly levels: readonly string[]
}

export type Verdict = 'CUKUP' | 'PAS' | 'KURANG'

export interface CategoryAssessment {
  readonly category: string
  readonly required: number
  readonly inCatalog: number
  readonly available: number
  readonly verdict: Verdict
  readonly message: string
}

export interface Assessment {
  readonly blueprint: string
  readonly rotations: number
  readonly feasible: boolean
  readonly categories: readonly CategoryAssessment[]
  readonly totalRequired: number
  readonly totalAvailable: number
  readonly problems: readonly string[]
}

export type ViolationKind = 'SESI_SAMA_HARI' | 'KARANTINA_TUK' | 'KARANTINA_NASIONAL'

export interface Violation {
  readonly stationSourceCode: string
  readonly kind: ViolationKind
  readonly conflictingSessionId: string
  readonly message: string
}

export interface Slot {
  readonly date: string
  readonly sessionOrder: number
  readonly tukId: string
}

export interface AssessResult {
  readonly slot: Slot
  readonly penilaian: Assessment
  readonly tertahanPaparan: readonly Violation[]
}

export interface CircuitSlot {
  readonly position: number
  readonly stationSourceCode: string
  readonly category: string
}

/**
 * Hasil penyusunan sirkuit.
 *
 * `berhasil` memisahkan kedua bentuk: gagal tidak membawa `sirkuit`, dan
 * berhasil tidak membawa `masalah`. Menyatukannya menjadi satu bentuk dengan
 * kedua field opsional membuat komponen dapat membaca sirkuit yang tidak ada.
 */
export type ComposeResult =
  | {
      readonly slot: Slot
      readonly seed: string
      readonly tertahanPaparan: number
      readonly berhasil: true
      readonly sirkuit: readonly CircuitSlot[]
    }
  | {
      readonly slot: Slot
      readonly seed: string
      readonly tertahanPaparan: number
      readonly berhasil: false
      readonly masalah: readonly string[]
    }

export async function list(): Promise<readonly Blueprint[]> {
  return request<readonly Blueprint[]>('/api/v1/blueprint')
}

export async function categories(): Promise<readonly Category[]> {
  return request<readonly Category[]>('/api/v1/blueprint/kategori')
}

export async function create(input: {
  name: string
  rotations: number
  notes: string | null
  requirements: readonly { category: string; minStations: number; minSkdiLevel?: string }[]
}): Promise<Blueprint> {
  return request<Blueprint>('/api/v1/blueprint', { method: 'POST', body: input })
}

export async function remove(id: string): Promise<void> {
  await request(`/api/v1/blueprint/${id}`, { method: 'DELETE' })
}

export async function assess(id: string, sessionId: string): Promise<AssessResult> {
  return request<AssessResult>(`/api/v1/blueprint/${id}/kecukupan`, {
    method: 'POST',
    body: { sessionId },
  })
}

export async function compose(
  id: string,
  sessionId: string,
  seed?: string,
): Promise<ComposeResult> {
  return request<ComposeResult>(`/api/v1/blueprint/${id}/susun-sirkuit`, {
    method: 'POST',
    body: { sessionId, ...(seed === undefined || seed === '' ? {} : { seed }) },
  })
}
